import axios from 'axios';
import { attachAuthRefresh } from './authRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = attachAuthRefresh(axios.create({
  baseURL: API,
  withCredentials: true
}));

export const eventClassesAPI = {
  list: () => api.get('/event-classes'),
  get: (slug) => api.get(`/event-classes/${slug}`),
  book: (slug, data) => api.post(`/event-classes/${slug}/book`, data),
  waitlist: (slug, data) => api.post(`/event-classes/${slug}/waitlist`, data),
  admin: {
    list: () => api.get('/admin/event-classes'),
    participants: (slug, params) => api.get(`/admin/event-classes/${slug}/participants`, { params }),
    exportXlsx: (slug, params) => api.get(`/admin/event-classes/${slug}/export.xlsx`, { params, responseType: 'blob' }),
    fulfilment: (bookingId, data) => api.patch(`/admin/event-classes/bookings/${bookingId}/fulfilment`, data),
    tracking: (slug, orderNumber, idx, data) => api.patch(`/admin/event-classes/${slug}/orders/${orderNumber}/participants/${idx}/tracking`, data),
    checkin: (slug, orderNumber, idx, data) => api.post(`/admin/event-classes/${slug}/orders/${orderNumber}/participants/${idx}/checkin`, data),
    settings: (slug) => api.get(`/admin/event-classes/${slug}/settings`),
    saveSettings: (slug, data) => api.put(`/admin/event-classes/${slug}/settings`, data),
    retryReminder: (id) => api.post(`/admin/event-classes/reminders/${id}/retry`),
    tick: () => api.post('/admin/event-classes/tick'),
    scheduleNotice: (slug, data) => api.post(`/admin/event-classes/${slug}/schedule-notice`, data),
    attendance: (slug, params) => api.get(`/admin/event-partners/attendance/${slug}`, { params }),
    partners: {
      forEvent: (slug) => api.get(`/admin/suppliers/for-event/${slug}`),
    },
    details: (orderNumber) => api.get(`/admin/event-classes/orders/${orderNumber}`),
    stripeSync: (orderNumber) => api.post(`/admin/event-classes/orders/${orderNumber}/stripe-sync`),
    editParticipant: (orderNumber, idx, data) => api.patch(`/admin/event-classes/orders/${orderNumber}/participants/${idx}`, data),
    cancelParticipant: (orderNumber, idx, data) => api.post(`/admin/event-classes/orders/${orderNumber}/participants/${idx}/cancel`, data),
    trashParticipant: (orderNumber, idx, data) => api.post(`/admin/event-classes/orders/${orderNumber}/participants/${idx}/trash`, data),
    restoreParticipant: (orderNumber, idx, data) => api.post(`/admin/event-classes/orders/${orderNumber}/participants/${idx}/restore`, data),
    purgeParticipant: (orderNumber, idx, reason) => api.delete(`/admin/event-classes/orders/${orderNumber}/participants/${idx}`, { params: { reason } }),
    bulkTrash: (data) => api.post('/admin/event-classes/bookings/bulk-trash', data),
    createRefund: (orderNumber, data) => api.post(`/admin/event-classes/orders/${orderNumber}/refunds`, data),
    confirmRefund: (refundId, data) => api.post(`/admin/event-classes/refunds/${refundId}/confirm`, data),
    voidRefund: (refundId, data) => api.post(`/admin/event-classes/refunds/${refundId}/void`, data),
    waitlist: (slug) => api.get(`/admin/event-classes/${slug}/waitlist`),
    notifyWaitlist: (slug) => api.post(`/admin/event-classes/${slug}/waitlist/notify`),
    removeWaitlist: (id) => api.post(`/admin/event-classes/waitlist/${id}/remove`),
  },
  dashboard: {
    list: (params) => api.get('/admin/event-dashboard', { params }),
    one: (kind, slug) => api.get(`/admin/event-dashboard/${kind}/${slug}`),
    setStatus: (kind, slug, data) => api.patch(`/admin/event-dashboard/${kind}/${slug}/status`, data),
  },
};


export const classesAPI = {
  getAll: (params) => api.get('/classes', { params }),
  getOne: (id) => api.get(`/classes/${id}`),
  create: (data) => api.post('/classes', data),
  patch: (id, data) => api.patch(`/classes/${id}`, data),
  delete: (id) => api.delete(`/classes/${id}`)
};

export const bookingsAPI = {
  getAll: () => api.get('/bookings'),
  create: (data) => api.post('/bookings', data),
  cancel: (id) => api.delete(`/bookings/${id}`),
  // Fetch a single booking (with class + coach + method_used + remaining) for
  // the post-booking "You're in!" confirmation page.
  getOne: (id) => api.get(`/me/bookings/${id}`),
};

export const packagesAPI = {
  getAll: () => api.get('/packages'),
  getAllAdmin: () => api.get('/packages/all'),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`),
  deletePermanent: (id) => api.delete(`/packages/${id}/permanent`),
  // Priority 1 — validity / extension / admin overrides
  myPackages: () => api.get('/me/packages'),
  startExtension: (userPackageId, body) => api.post(`/me/packages/${userPackageId}/extend/start`, body || {}),
  freeze: (userPackageId, reason = '') => api.post(`/me/packages/${userPackageId}/freeze`, { reason }),
  reactivate: (userPackageId, reason = '') => api.post(`/me/packages/${userPackageId}/reactivate`, { reason }),
  adminList: () => api.get('/admin/user-packages'),
  adminAdjust: (id, data) => api.patch(`/admin/user-packages/${id}`, data),
  adminAudit: (limit = 200) => api.get(`/admin/package-audit?limit=${limit}`),
  adminFreeze: (id, reason = '') => api.post(`/admin/user-packages/${id}/freeze`, { reason }),
  adminReactivate: (id, reason = '') => api.post(`/admin/user-packages/${id}/reactivate`, { reason }),
  adminMerge: (sourceId, targetId, reason = '') => api.post(`/admin/user-packages/${sourceId}/merge`, { target_id: targetId, reason }),
  adminSplit: (sourceId, data) => api.post(`/admin/user-packages/${sourceId}/split`, data),
  // Aug 2026 — customer-submitted freeze / extension requests (admin approves)
  requestFreeze: (userPackageId, reason = '') => api.post(`/me/packages/${userPackageId}/request-freeze`, { reason }),
  requestExtension: (userPackageId, reason = '') => api.post(`/me/packages/${userPackageId}/request-extension`, { reason }),
  // Package Transfer (Feb 2026) — parent reassigns an unused class package
  // from one family profile to another on the same account. Full remaining
  // balance moves; expiry / purchase history stays put.
  transfer: (userPackageId, targetProfileId) =>
    api.post(`/me/packages/${userPackageId}/transfer`, { target_profile_id: targetProfileId || '' }),
  // Customer + admin package-request workflow (freeze / extension approvals).
  myRequests: () => api.get('/me/package-requests'),
  adminListRequests: (status) => api.get('/admin/package-requests', { params: status ? { status } : {} }),
  adminApproveFreeze: (requestId, data) => api.post(`/admin/package-requests/${requestId}/approve-freeze`, data),
  adminApproveExtension: (requestId, data) => api.post(`/admin/package-requests/${requestId}/approve-extension`, data),
  adminRejectRequest: (requestId, admin_notes = '') => api.post(`/admin/package-requests/${requestId}/reject`, { admin_notes }),
};

// ── Private Coaching Pricing (Feb 2026 · Msg 179 follow-up) ─────────────
// Single source of truth for the customer-facing price guide, live quotes
// and admin editing surface. Every quote endpoint reads settings from the
// same MongoDB doc, so displayed and charged amounts always match.
export const privatePricingAPI = {
  guide: () => api.get('/private/pricing-guide'),
  adminGet: () => api.get('/admin/private-pricing/settings'),
  adminUpdate: (patch, reason, effective_from = null) =>
    api.put('/admin/private-pricing/settings', { patch, reason, effective_from }),
  adminHistory: (limit = 50) =>
    api.get('/admin/private-pricing/history', { params: { limit } }),
  adminManualAdjustBooking: (bookingId, newAmountCents, reason) =>
    api.post(`/admin/private-bookings/${bookingId}/manual-adjust`, {
      new_amount_cents: newAmountCents,
      reason,
    }),
};
// Backend refuses non-full plans without an explicit terms acceptance and
// stamps every acceptance on the order + plan for audit.
export const installmentsAPI = {
  // Public / customer
  preview: (amountCents, includeLegacy = false) =>
    api.get('/installments/preview', {
      params: { amount_cents: amountCents, include_legacy: includeLegacy },
    }),
  terms: () => api.get('/installments/terms'),
  myPlans: () => api.get('/me/installments'),
  activeReminders: (daysAhead = 5) =>
    api.get('/me/installments/active-reminders', { params: { days_ahead: daysAhead } }),
  getPlan: (planId) => api.get(`/me/installments/${planId}`),
  payNext: (planId) => api.post(`/me/installments/${planId}/pay-next`),
  payRemaining: (planId) => api.post(`/me/installments/${planId}/pay-remaining`),
  // Split checkout status (Feb 2026 · cart-level installments)
  splitStatus: (checkoutGroupId) =>
    api.get(`/checkout/cart/split-status/${checkoutGroupId}`),
  // Admin
  adminList: (params = {}) => api.get('/admin/installments', { params }),
  adminGetPlan: (planId) => api.get(`/admin/installments/${planId}`),
  adminSettings: () => api.get('/admin/installments/settings'),
  adminUpdateSettings: (patch) => api.put('/admin/installments/settings', patch),
  adminWaiveLateCharge: (planId, scheduleId, reason) =>
    api.post(`/admin/installments/${planId}/waive-late-charge`, { schedule_id: scheduleId, reason }),
  adminToggleSuspension: (planId, suspend, reason) =>
    api.post(`/admin/installments/${planId}/toggle-suspension`, { suspend, reason }),
  adminWaLink: (planId) => api.get(`/admin/installments/${planId}/whatsapp-link`),
  adminResendReminder: (planId) => api.post(`/admin/installments/${planId}/resend-reminder`),
  // Admin Special-Case Approval Workflow (Feb 2026)
  adminCreateChangeRequest: (planId, body) =>
    api.post(`/admin/installments/${planId}/change-requests`, body),
  adminListChangeRequests: (params = {}) =>
    api.get('/admin/installment-change-requests', { params }),
  adminReviewChangeRequest: (requestId, body) =>
    api.post(`/admin/installment-change-requests/${requestId}/review`, body),
};

export const notificationsAPI = {
  list: (params = {}) => api.get('/me/notifications', { params }),
  unreadCount: () => api.get('/me/notifications/unread-count'),
  markRead: (id) => api.post(`/me/notifications/${id}/read`),
  markAllRead: () => api.post('/me/notifications/read-all'),
  dismiss: (id) => api.post(`/me/notifications/${id}/dismiss`),
  restore: (id) => api.post(`/me/notifications/${id}/restore`),
};

export const paymentsAPI = {
  create: (data) => api.post('/payments', data),
  createDropin: (data) => api.post('/payments/dropin', data),
  // Auth-optional package checkout — works for both logged-in members (uses
  // their tier price) and guests (accepts full_name/email/phone in the body).
  checkoutPackage: (data) => api.post('/checkout/package', data),
  getStatus: (order) => api.get(`/payments/${order}`)
};

// ── Cash-refund request workflow (Aug 2026) ─────────────────────────────
export const refundsAPI = {
  // Customer
  getPolicy: () => api.get('/refund-requests/policy'),
  submit: (formData) => api.post('/refund-requests', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  myRequests: () => api.get('/me/refund-requests'),
  // Admin
  adminList: (status) => api.get('/admin/refund-requests', { params: status ? { status } : {} }),
  adminApprove: (id, admin_notes = '') => api.post(`/admin/refund-requests/${id}/approve`, { admin_notes }),
  adminReject: (id, admin_notes) => api.post(`/admin/refund-requests/${id}/reject`, { admin_notes }),
  adminTrigger: (id, data) => api.post(`/admin/refund-requests/${id}/trigger-refund`, data),
  // Attachments — returns blob for inline display / download.
  attachmentUrl: (requestId, filename) => `${API}/refund-attachments/${requestId}/${filename}`,
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getOne: (id) => api.get(`/users/${id}`),
  updateProfile: (data) => api.put('/auth/me', data),
  updateCommunicationPreferences: (data) =>
    api.patch('/me/profile/communication-preferences', data),
};

export const CLASS_TYPES = ['kids', 'teens', 'adult', 'enhancement'];

export const CLASS_TYPE_LABELS = {
  kids: 'Kids',
  teens: 'Teens',
  adult: 'Adult',
  enhancement: 'Enhancement'
};

export const CLASS_TYPE_COLORS = {
  kids: 'bg-pink-100 text-pink-800 border-pink-300',
  teens: 'bg-blue-100 text-blue-800 border-blue-300',
  adult: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  enhancement: 'bg-violet-100 text-violet-800 border-violet-300'
};

export const bucketLabel = (bucketKey) => {
  if (!bucketKey) return 'General';
  const types = bucketKey.split(',');
  if (types.length === 1) return CLASS_TYPE_LABELS[types[0]] || types[0];
  if (types.length === 4) return 'Universal';
  return types.map((t) => CLASS_TYPE_LABELS[t] || t).join(' / ');
};

export const coachesAPI = {
  getAll: () => api.get('/coaches')
};

export const recurringSchedulesAPI = {
  list: () => api.get('/admin/recurring-schedules'),
  migrate: () => api.post('/admin/recurring-schedules/migrate'),
  preview: (ruleId) => api.post('/admin/recurring-schedules/preview', { rule_id: ruleId || null }),
  generate: (ruleId) => api.post('/admin/recurring-schedules/generate', { rule_id: ruleId || null, confirm: true }),
  create: (data) => api.post('/admin/recurring-schedules', data),
  update: (id, data) => api.patch(`/admin/recurring-schedules/${id}`, data),
  pause: (id) => api.post(`/admin/recurring-schedules/${id}/pause`),
  reactivate: (id) => api.post(`/admin/recurring-schedules/${id}/reactivate`),
  excludeDates: (id, dates, action) => api.post(`/admin/recurring-schedules/${id}/exclude-dates`, { dates, action }),
  remove: (id) => api.delete(`/admin/recurring-schedules/${id}`),
};

export const attendanceAPI = {
  mark: (data) => api.post('/attendance', data),
  get: (params) => api.get('/attendance', { params }),
  getClassBookings: (classId) => api.get(`/classes/${classId}/bookings`)
};

export const reportsAPI = {
  getOverview: () => api.get('/reports/overview')
};

export const alertsAPI = {
  list: (includeAcknowledged = false) =>
    api.get('/admin/alerts', { params: { include_acknowledged: includeAcknowledged } }),
  acknowledge: (id) => api.post(`/admin/alerts/${id}/acknowledge`),
  checkNow: () => api.post('/admin/alerts/check-now')
};

export const findClassAPI = {
  submit: (data) => api.post('/find-my-class', data),
  listSubmissions: (includeClosed = false) =>
    api.get('/admin/find-class-submissions', { params: { include_closed: includeClosed } }),
  closeSubmission: (id) => api.post(`/admin/find-class-submissions/${id}/close`)
};

export const privateAPI = {
  getConfig: () => api.get('/private/config'),
  quote: (data) => api.post('/private/quote', data),
  submitEnquiry: (data) => api.post('/private/enquiry', data),
  listEnquiries: (includeClosed = false) =>
    api.get('/admin/private/enquiries', { params: { include_closed: includeClosed } }),
  closeEnquiry: (id) => api.post(`/admin/private/enquiries/${id}/close`),
  updateEnquiry: (id, data) => api.patch(`/admin/private/enquiries/${id}`, data),
  deleteEnquiry: (id) => api.delete(`/admin/private/enquiries/${id}`),
  // Booking v2
  listLocations: () => api.get('/private/locations'),
  createBooking: (data) => api.post('/private/bookings', data),
  myBookings: () => api.get('/me/private-bookings'),
  acceptReschedule: (id, proposal_index) => api.post(`/me/private-bookings/${id}/accept-reschedule`, { proposal_index }),
  rescheduleBooking: (id, data) => api.post(`/me/private-bookings/${id}/reschedule`, data),
  adminListBookings: () => api.get('/admin/private-bookings'),
  adminConfirmBooking: (id, assigned_coach_id = null, note = '') =>
    api.post(`/admin/private-bookings/${id}/confirm`, null, { params: { assigned_coach_id, note } }),
  adminProposeReschedule: (id, proposals, note = '') =>
    api.post(`/admin/private-bookings/${id}/propose-reschedule`, { proposals, note }),
  adminMarkNoShow: (id, note = '') =>
    api.post(`/admin/private-bookings/${id}/mark-no-show`, null, { params: { note } }),
  adminComplete: (id, note = '') =>
    api.post(`/admin/private-bookings/${id}/complete`, null, { params: { note } }),
  // Coach-scoped
  coachListBookings: () => api.get('/coach/private-bookings'),
  coachComplete: (id, note = '') =>
    api.post(`/coach/private-bookings/${id}/complete`, null, { params: { note } }),
  coachMarkNoShow: (id, note = '') =>
    api.post(`/coach/private-bookings/${id}/mark-no-show`, null, { params: { note } }),
};

export const waitlistAPI = {
  // Student-facing
  join: (classId) => api.post('/waitlist/join', { class_id: classId }),
  myWaitlists: () => api.get('/me/waitlists'),
  cancel: (entryId) => api.delete(`/waitlist/${entryId}`),
  lookupOffer: (token) => api.get(`/waitlist/offer/${token}`),
  startConfirm: (token) => api.post(`/waitlist/offer/${token}/start`),
  finalizeBooking: (entryId) => api.post(`/waitlist/${entryId}/finalize-booking`),
  publicSummary: (classId) => api.get(`/public/waitlist-summary/${classId}`),
  // Admin-facing
  adminList: (params) => api.get('/admin/waitlists', { params }),
  adminAddWalkin: (data) => api.post('/admin/waitlists/add-walkin', data),
  adminOffer: (entryId, reason) => api.post(`/admin/waitlists/${entryId}/offer`, { reason }),
  adminSkip: (entryId, reason) => api.post(`/admin/waitlists/${entryId}/skip`, { reason }),
  adminRemove: (entryId, reason) => api.post(`/admin/waitlists/${entryId}/remove`, { reason }),
  adminReorder: (entryId, positionHint, reason) =>
    api.post(`/admin/waitlists/${entryId}/reorder`, { position_hint: positionHint, reason }),
  adminAudit: (entryId) => api.get(`/admin/waitlists/${entryId}/audit`),
  adminSweep: (classId) => api.post(`/admin/waitlists/${classId}/sweep`),
};

export const subscriptionAPI = {
  plans: () => api.get('/subscription/plans'),
  mySubscription: () => api.get('/me/subscription'),
  checkout: (data) => api.post('/subscription/checkout', data),
  cancel: () => api.post('/subscription/cancel'),
  resume: () => api.post('/subscription/resume'),
  scheduleUpgrade: (newTier) => api.post('/subscription/schedule-upgrade', { new_tier: newTier }),
  // Admin
  adminSettings: () => api.get('/admin/subscription-settings'),
  adminUpdateSettings: (tier, data) => api.put(`/admin/subscription-settings/${tier}`, data),
  adminCreateSettings: (data) => api.post('/admin/subscription-settings', data),
  adminDeleteSettings: (tier) => api.delete(`/admin/subscription-settings/${tier}`),
  adminList: (params) => api.get('/admin/subscriptions', { params }),
  adminActivateManual: (subId) => api.post(`/admin/subscriptions/${subId}/activate-manual`),
};

// Family multi-profile — Feb 2026. One parent account can own multiple
// participants (children AND adult family members). Every booking picks the
// attending profile so booking history, attendance and medical notes stay
// per-person.
export const familyAPI = {
  list: () => api.get('/me/family'),
  create: (data) => api.post('/me/family', data),
  update: (id, data) => api.patch(`/me/family/${id}`, data),
  archive: (id) => api.delete(`/me/family/${id}`),
  setLogin: (id, data) => api.post(`/me/family/${id}/login`, data),
  removeLogin: (id) => api.delete(`/me/family/${id}/login`),
  uploadPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/me/family/${id}/photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (id) => api.delete(`/me/family/${id}/photo`),
  photoUrl: (id) => `${API}/me/family/${id}/photo`,
  // Admin overrides
  adminList: (params) => api.get('/admin/family', { params }),
  adminUpdate: (id, data) => api.patch(`/admin/family/${id}`, data),
  adminArchive: (id) => api.delete(`/admin/family/${id}`),
  adminDeletePermanent: (id) => api.delete(`/admin/family/${id}/permanent`),
};


export const aiImagesAPI = {
  generate: (data) => api.post('/ai-images/generate', data),
  list: (params) => api.get('/ai-images', { params }),
  remove: (id) => api.delete(`/ai-images/${id}`),
  fileUrl: (id) => `${API}/ai-images/${id}/file`,
};

/** LGRA Admin → Supplier Management (Super Admin only on the backend). */
export const suppliersAPI = {
  catalog: () => api.get('/admin/suppliers/catalog'),
  list: () => api.get('/admin/suppliers'),
  create: (data) => api.post('/admin/suppliers', data),
  get: (id) => api.get(`/admin/suppliers/${id}`),
  patch: (id, data) => api.patch(`/admin/suppliers/${id}`, data),
  createUser: (id, data) => api.post(`/admin/suppliers/${id}/users`, data),
  patchUser: (id, uid, data) => api.patch(`/admin/suppliers/${id}/users/${uid}`, data),
  invite: (id, uid) => api.post(`/admin/suppliers/${id}/users/${uid}/invite`),
  assignEvent: (id, slug, data) => api.put(`/admin/suppliers/${id}/events/${slug}`, data),
  unassignEvent: (id, slug) => api.delete(`/admin/suppliers/${id}/events/${slug}`),
  searchActivities: (params) => api.get('/admin/suppliers/activities/search', { params }),
  categories: () => api.get('/admin/suppliers/activities/categories'),
  createCategory: (label) => api.post('/admin/suppliers/activities/categories', { label }),
  customActivities: () => api.get('/admin/suppliers/activities/custom'),
  createCustomActivity: (data) => api.post('/admin/suppliers/activities/custom', data),
  shareActivity: (id, data) => api.post(`/admin/suppliers/${id}/activities`, data),
  updateActivity: (id, aid, data) => api.patch(`/admin/suppliers/${id}/activities/${aid}`, data),
  revokeActivity: (id, aid) => api.delete(`/admin/suppliers/${id}/activities/${aid}`),
  managers: () => api.get('/admin/suppliers/managers'),
  setManager: (uid, enabled) => api.put(`/admin/suppliers/managers/${uid}`, { enabled }),
  assignProduct: (id, pid) => api.put(`/admin/suppliers/${id}/products/${pid}`),
  unassignProduct: (id, pid) => api.delete(`/admin/suppliers/${id}/products/${pid}`),
  createLocation: (id, data) => api.post(`/admin/suppliers/${id}/locations`, data),
  deleteLocation: (id, lid) => api.delete(`/admin/suppliers/${id}/locations/${lid}`),
  allocate: (id, data) => api.post(`/admin/suppliers/${id}/stock/allocate`, data),
  movement: (id, data) => api.post(`/admin/suppliers/${id}/stock/movement`, data),
  stock: (id, params) => api.get(`/admin/suppliers/${id}/stock`, { params }),
  audit: (id) => api.get(`/admin/suppliers/${id}/audit`),
  requests: (status) => api.get('/admin/suppliers/requests', { params: { status } }),
  decide: (rid, data) => api.post(`/admin/suppliers/requests/${rid}/decide`, data),
};

export default api;

